import Button from '@/components/Button';
import CreateLazbubu from '@/components/CreateLazbubu';
import RarityBadge from '@/components/RarityBadge';
import RedeemModal from '@/components/RedeemModal';
import { isBSCNetwork } from '@/constant';
import { getServerApi } from '@/gql';
import application from '@/stores/application';
import user from '@/stores/user';
import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import bindboxImg from '../../assets/images/bindbox.png';
import { BoxGrapText } from '../UI';

// Styled Components
const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 2rem;
`;

// Flip Card Container
const FlipCardContainer = styled.div`
    perspective: 1000px;
    width: 100%;
    max-width: 400px;
    height: 630px;
    position: relative;
`;

const FlipCardInner = styled.div<{ $isFlipped: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    text-align: center;
    transition: transform 0.8s;
    transform-style: preserve-3d;
    transform: ${({ $isFlipped }) =>
        $isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const FlipCardFace = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    border-radius: 24px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const FlipCardFront = styled(FlipCardFace)`
    background: transparent;
`;

const FlipCardBack = styled(FlipCardFace)`
    background: transparent;
    transform: rotateY(180deg);
    overflow-y: auto;
`;

const ContentWrapper = styled.div`
    border-radius: 24px;
    padding: 1rem;
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
`;

const Title = styled(Typography)`
    font-size: 20px;
    color: #000;
    font-weight: 600;
`;

const BoxImageContainer = styled.div`
    border-radius: 20px;
    padding: 2rem;
    margin-bottom: 1.5rem;
    position: relative;
    overflow: hidden;
`;

const BoxImage = styled.img`
    width: 100%;
    width: 240px;
    height: auto;
    object-fit: contain;
    border-radius: 32px;
`;

const RarityText = styled(Typography)`
    font-size: 14px;
    color: #000;
    margin-bottom: 1.5rem;
`;

const RarityButtonsContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    justify-content: center;
`;

const RarityButton = styled.div<{ $rarity: string }>`
    padding: 8px 12px;
    border: none;
    border-radius: 10px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    ${({ $rarity }) => {
        switch ($rarity) {
            case 'common':
                return `
          background: #39B6BF;
          color: white;
       
        `;
            case 'rare':
                return `
          background: #2C44B0;
          color: white;
     
        `;
            case 'epic':
                return `
          background: #6900D2;
          color: white;
      
        `;
            case 'legendary':
                return `
          background: #FC7186;
          color: white;
        
        `;
            default:
                return `
          background: #6b7280;
          color: white;
        `;
        }
    }}
`;

const PageButton = styled(Button)`
    width: 160px !important;
`;

// Rarity data
const rarities = [
    { key: 'common', label: 'Common', color: '#3b82f6' },
    { key: 'rare', label: 'Rare', color: '#8b5cf6' },
    { key: 'epic', label: 'Epic', color: '#6366f1' },
    { key: 'legendary', label: 'Legendary', color: '#ec4899' },
];

const BlindBox: React.FC = observer(() => {
    const history = useHistory();
    const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
    const [isFlipped, setIsFlipped] = useState(false);
    const [redeemLazbubuData, setRedeemLazbubuData] = useState<string>('');
    const [redeemCode, setRedeemCode] = useState<string>('');
    const handleOpenBox = () => {
        // Generate a random ID for the new Lazbubu

        setIsFlipped(true);
    };

    const handleRedeemCode = () => {
        setIsRedeemModalOpen(true);
    };

    async function getLazbubuData(code: string) {
        const res = await getServerApi().lazbubuData({
            activationCode: code,
        });
        return res.lazbubuData;
    }

    async function refetchLazbubuDataByCode(code: string) {
        const lazbubuData = await getLazbubuData(code);
        if (lazbubuData) {
            setRedeemLazbubuData(lazbubuData);
        }
    }

    const handleRedeemSuccess = async (code: string) => {
        setRedeemCode(code);
        const lazbubuData = await getLazbubuData(code);
        if (lazbubuData) {
            setRedeemLazbubuData(lazbubuData);
            setIsFlipped(true);
        }
    };

    const handleRedeemError = (error: string) => {
        console.error('Redeem failed:', error);
    };

    return (
        <PageContainer>
            <FlipCardContainer>
                <FlipCardInner $isFlipped={isFlipped}>
                    <FlipCardFront>
                        <ContentWrapper>
                            <Title>Draw a Lazbubu!</Title>

                            <BoxImageContainer>
                                <BoxImage
                                    src={bindboxImg}
                                    alt="Lazbubu Blind Box"
                                />
                            </BoxImageContainer>

                            <RarityText>
                                Each Lazbubu comes in different rarity :
                            </RarityText>

                            <RarityButtonsContainer>
                                <RarityBadge rarity="common" />
                                <RarityBadge rarity="advanced" />
                                <RarityBadge rarity="rare" />
                                <RarityBadge rarity="epic" />
                            </RarityButtonsContainer>
                            {!!user.connected && !!user.userId ? (
                                <>
                                    {!user.hasBlindBox && !isBSCNetwork && (
                                        <BoxGrapText />
                                    )}
                                    {user.canOpenBlindBox ? (
                                        <PageButton
                                            size="small"
                                            onClick={handleOpenBox}
                                        >
                                            Open box
                                        </PageButton>
                                    ) : !!user.userLazbubuInfo
                                          ?.lazbubuCreated ? (
                                        <PageButton
                                            size="small"
                                            onClick={handleRedeemCode}
                                            disabled
                                        >
                                            Lazbubu minted
                                        </PageButton>
                                    ) : !user.userLazbubuInfo
                                          ?.lazbubuCreated ? (
                                        // <PageButton
                                        //     size="small"
                                        //     onClick={handleRedeemCode}
                                        // >
                                        //     Redeem code
                                        //             </PageButton>
                                        isBSCNetwork ? (
                                            <PageButton size="small" disabled>
                                                Not eligible
                                            </PageButton>
                                        ) : (
                                            <PageButton
                                                size="small"
                                                onClick={() => {
                                                    application.closeExploreNavDrawer();
                                                    history.push(
                                                        '/lazbubu/boxdrop',
                                                    );
                                                }}
                                            >
                                                Grab your box
                                            </PageButton>
                                        )
                                    ) : null}
                                </>
                            ) : (
                                <PageButton auth size="small">
                                    Connect wallet
                                </PageButton>
                            )}
                        </ContentWrapper>
                    </FlipCardFront>

                    <FlipCardBack>
                        {redeemLazbubuData ? (
                            <CreateLazbubu
                                lazbubuData={redeemLazbubuData}
                                refetchLazbubuData={async () => {
                                    refetchLazbubuDataByCode(redeemCode);
                                }}
                                canClearLazbubuData={false}
                            />
                        ) : (
                            <CreateLazbubu
                                lazbubuData={
                                    user?.userLazbubuInfo?.lazbubuData || ''
                                }
                                refetchLazbubuData={user.getLazbubuData}
                                onCreateSuccess={() => {
                                    user.getLazbubuData();
                                    // setIsFlipped(false);
                                }}
                            />
                        )}
                    </FlipCardBack>
                </FlipCardInner>
            </FlipCardContainer>

            <RedeemModal
                isOpen={isRedeemModalOpen}
                onClose={() => setIsRedeemModalOpen(false)}
                handleSuccess={handleRedeemSuccess}
                handleError={handleRedeemError}
            />
        </PageContainer>
    );
});

export default BlindBox;
